import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableVideoCard } from './SortableVideoCard';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface PresentationVideo {
  id: string;
  title: string;
  description: string | null;
  youtube_url: string;
  category: string;
  display_order: number;
}

interface SortableVideoGridProps {
  videos: PresentationVideo[];
  isAdmin: boolean;
  onEdit: (video: PresentationVideo) => void;
  onDelete: (video: PresentationVideo) => void;
  onReorder: (videos: PresentationVideo[]) => void;
}

export const SortableVideoGrid = ({ 
  videos, 
  isAdmin, 
  onEdit, 
  onDelete,
  onReorder 
}: SortableVideoGridProps) => {
  const { toast } = useToast();
  const { t } = useTranslation('presentation');
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = videos.findIndex((v) => v.id === active.id);
      const newIndex = videos.findIndex((v) => v.id === over.id);
      
      const reorderedVideos = arrayMove(videos, oldIndex, newIndex);
      
      // Update local state immediately for responsiveness
      onReorder(reorderedVideos);
      
      // Update database
      try {
        const updates = reorderedVideos.map((video, index) => ({
          id: video.id,
          display_order: index,
        }));

        for (const update of updates) {
          const { error } = await supabase
            .from('presentation_videos')
            .update({ display_order: update.display_order })
            .eq('id', update.id);

          if (error) throw error;
        }

        toast({
          title: t('messages.orderUpdated'),
          description: t('messages.orderUpdatedDesc'),
        });
      } catch (error) {
        console.error('Error updating order:', error);
        toast({
          title: t('messages.error'),
          description: t('messages.errorUpdatingOrder'),
          variant: 'destructive',
        });
      }
    }
  };

  if (!isAdmin) {
    // Non-admin view: regular grid without drag
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <SortableVideoCard
            key={video.id}
            video={video}
            isAdmin={false}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={videos.map(v => v.id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <SortableVideoCard
              key={video.id}
              video={video}
              isAdmin={isAdmin}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
